import axios from 'axios';
import { AddressResult } from '../types';
import { validateSearchQuery, sanitizeSearchInput } from '../utils/validation';

const API_BASE_URL = 'https://www.map.gov.hk/gs/api/v1.0.0/locationSearch';

// Rate limiting cache
const requestCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const lastRequestTime = { value: 0 };
const MIN_REQUEST_INTERVAL = 300; // 300ms between requests

export interface SearchResponse {
    results: AddressResult[];
    error?: string;
    total?: number;
}

export const searchAddresses = async (query: string): Promise<SearchResponse> => {
    try {
        // Validate input
        const validation = validateSearchQuery(query);
        if (!validation.isValid) {
            return {
                results: [],
                error: validation.error
            };
        }
        
        // Sanitize input
        const sanitizedQuery = sanitizeSearchInput(query);
        const cacheKey = sanitizedQuery.toLowerCase();
        
        // Check cache first
        const cached = requestCache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            return {
                results: cached.data,
                total: cached.data.length
            };
        }
        
        // Rate limiting
        const now = Date.now();
        const timeSinceLastRequest = now - lastRequestTime.value;
        if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
            await new Promise(resolve => 
                setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest)
            );
        }
        lastRequestTime.value = Date.now();
        
        const response = await axios.get(`${API_BASE_URL}?q=${encodeURIComponent(sanitizedQuery)}`, {
            timeout: 10000, // 10 second timeout
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        });
        
        const data = response.data as any;
        
        // Handle different possible response formats
        let results: any[] = [];
        if (data.results && Array.isArray(data.results)) {
            results = data.results;
        } else if (Array.isArray(data)) {
            results = data;
        } else if (data.data && Array.isArray(data.data)) {
            results = data.data;
        } else if (data.features && Array.isArray(data.features)) {
            results = data.features;
        }
        
        // Transform the API response to match our interface
        const transformedResults = results
            .map((result: any, index: number) => {
                // Extract coordinates - Hong Kong Geodata API uses x, y coordinates
                let latitude = 22.3193; // Default Hong Kong coordinates
                let longitude = 114.1694;
                
                if (result.x && result.y) {
                    // Hong Kong Geodata API uses x, y coordinates (Hong Kong 1980 Grid System)
                    // We need to convert these to latitude/longitude
                    // For now, using approximate conversion
                    longitude = 114.1694 + (result.x - 840000) / 10000;
                    latitude = 22.3193 + (result.y - 820000) / 10000;
                } else if (result.geometry && result.geometry.coordinates) {
                    // GeoJSON format: [longitude, latitude]
                    longitude = Number(result.geometry.coordinates[0]);
                    latitude = Number(result.geometry.coordinates[1]);
                } else if (result.latitude && result.longitude) {
                    latitude = Number(result.latitude);
                    longitude = Number(result.longitude);
                } else if (result.lat && result.lng) {
                    latitude = Number(result.lat);
                    longitude = Number(result.lng);
                }
                
                // Extract name and address - Hong Kong Geodata API uses nameEN, nameZH, addressEN, addressZH
                const nameEn = result.nameEN || result.nameEn || result.name || result.title || '';
                const nameZh = result.nameZH || result.nameZh || result.name || result.title || '';
                const addressEn = result.addressEN || result.addressEn || result.address || result.description || '';
                const addressZh = result.addressZH || result.addressZh || result.address || result.description || '';
                
                // Extract district information
                const districtEn = result.districtEN || result.districtEn || result.district || result.region || '';
                const districtZh = result.districtZH || result.districtZh || result.district || result.region || '';
                
                // Use English name as primary name, fallback to Chinese if English is empty
                const name = nameEn || nameZh || `Location ${index + 1}`;
                const address = addressEn || addressZh || name;
                const district = districtEn || districtZh || '';
                
                return {
                    id: result.id || `result-${index}`,
                    name: name,
                    nameEn: nameEn || name,
                    nameZh: nameZh || name,
                    address: address,
                    addressEn: addressEn || address,
                    addressZh: addressZh || address,
                    district: district,
                    districtEn: districtEn || district,
                    districtZh: districtZh || district,
                    coordinates: {
                        latitude: typeof latitude === 'string' ? parseFloat(latitude) : latitude,
                        longitude: typeof longitude === 'string' ? parseFloat(longitude) : longitude
                    }
                };
            })
            // Filter out results that don't have meaningful address information
            .filter((result: AddressResult, index: number) => {
                // Get the original API result to check for actual addressEN/addressZH values
                const originalResult = results[index];
                
                // Check if the original API response has actual addressEN or addressZH values
                const hasOriginalAddressEn = originalResult.addressEN || originalResult.addressEn;
                const hasOriginalAddressZh = originalResult.addressZH || originalResult.addressZh;
                
                // Check if the result has meaningful address information
                const hasAddressEn = result.addressEn && result.addressEn.trim().length > 0;
                const hasAddressZh = result.addressZh && result.addressZh.trim().length > 0;
                const hasNameEn = result.nameEn && result.nameEn.trim().length > 0;
                const hasNameZh = result.nameZh && result.nameZh.trim().length > 0;
                
                // Return true if we have at least one meaningful address field AND at least one meaningful name field
                // AND the original API response had actual addressEN or addressZH values
                return (hasAddressEn || hasAddressZh) && (hasNameEn || hasNameZh) && (hasOriginalAddressEn || hasOriginalAddressZh);
            });
        
        // Cache successful results
        requestCache.set(cacheKey, {
            data: transformedResults,
            timestamp: Date.now()
        });
        
        // Clean old cache entries
        requestCache.forEach((value, key) => {
            if (Date.now() - value.timestamp > CACHE_TTL) {
                requestCache.delete(key);
            }
        });
        
        return {
            results: transformedResults,
            total: transformedResults.length
        };
        
    } catch (error) {
        // Enhanced error handling with specific error messages
        if (error instanceof Error) {
            // Input validation errors
            if (error.message.includes('Search query')) {
                return {
                    results: [],
                    error: error.message
                };
            }
        }
        
        if (axios.isAxiosError(error)) {
            if (error.code === 'ECONNABORTED') {
                return {
                    results: [],
                    error: 'Search request timed out. Please try again.'
                };
            }
            
            if (error.response?.status === 429) {
                return {
                    results: [],
                    error: 'Too many requests. Please wait a moment and try again.'
                };
            }
            
            if (error.response && error.response.status >= 500) {
                return {
                    results: [],
                    error: 'Server is temporarily unavailable. Please try again later.'
                };
            }
            
            if (!navigator.onLine) {
                return {
                    results: [],
                    error: 'No internet connection. Please check your network and try again.'
                };
            }
        }
        
        return {
            results: [],
            error: 'Failed to search addresses. Please try again.'
        };
    }
};

export const generateGoogleMapsUrl = (address: string): string => {
    // Use the address directly in the Google Maps URL
    return `https://www.google.com/maps/search/${encodeURIComponent(address)}`;
}; 